import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarDays, Droplets, Flame, MoonStar, X } from "lucide-react";
import { scrollToId } from "../../lib/api";
import { todayIrrigation, useGrow } from "../../lib/irrigation";
import { JStreak, useStreak } from "./JStreak";

// Each product is watered by the animal on its label (Compact = the rock).
const WATERER = {
  grow: ["/img/water-giraffe.webp", "La jirafa"],
  "a-bloom": ["/img/water-toucan.webp", "El tucán"],
  "b-bloom": ["/img/water-toucan.webp", "El tucán"],
  "pk-065": ["/img/water-elephant.webp", "El elefante"],
  "bongo-bloom": ["/img/water-monkey.webp", "El mono"],
  compact: ["/img/water-rock.webp", "La roca"],
};
const dayName = (d) => d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "short" });

const pour = (i) => ({ animate: { rotate: [0, -8, 0, -5, 0], y: [0, -2, 0] }, transition: { duration: 2.4, repeat: Infinity, delay: i * 0.35, ease: "easeInOut" } });

const Panel = ({ today, streak, onClose }) => (
  <motion.div data-testid="water-today-panel" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6, scale: 0.95 }}
    transition={{ type: "spring", stiffness: 360, damping: 26 }} style={{ transformOrigin: "0% 0%" }}
    className="absolute top-[calc(100%+10px)] left-0 w-[min(300px,calc(100vw-24px))] rounded-[16px] bg-[#F6EEDC] text-[#0E2A1C] p-4 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.7)] border-2 border-[#5B7FE0]">
    <button onClick={onClose} aria-label="Cerrar" data-testid="water-today-close" className="absolute top-2.5 right-2.5 w-6 h-6 grid place-items-center rounded-full hover:bg-[#0E2A1C]/10 transition-colors"><X className="w-3.5 h-3.5" /></button>
    <p className="font-mono-d text-[10px] tracking-[0.18em] uppercase text-[#1B3A8C]">{today ? `Hoy · ${today.label}` : "Tu cuadrante de riego"}</p>
    <p data-testid="water-today-title" className="font-serif-d font-black text-2xl mt-1">{!today ? "Planifica tu riego" : today.water ? "Hoy riegas" : "Hoy descansas"}</p>
    {today?.water && (
      <ul className="mt-3 space-y-2">
        {today.doses.map((d, i) => (
          <li key={d.id} data-testid={`water-today-item-${d.id}`} className="flex items-center gap-3">
            <motion.img {...pour(i)} src={WATERER[d.id]?.[0]} alt={`${WATERER[d.id]?.[1]} regando ${d.name}`} className="w-14 h-14 object-contain" style={{ transformOrigin: "50% 90%" }} />
            <span className="text-sm leading-tight"><b>{d.name}</b><br /><span className="text-[#1B3A8C] font-mono-d text-xs">{d.dose} mL/L</span></span>
          </li>
        ))}
      </ul>
    )}
    {today && !today.water && <p className="text-sm mt-2">Deja reposar el sustrato.{today.next && <> Próximo riego: <b className="capitalize">{dayName(today.next.date)}</b>.</>}</p>}
    {today?.water && today.doses.length === 0 && <p data-testid="water-today-flush" className="text-sm mt-2">Semana de lavado: riega solo con agua, sin nutrientes.</p>}
    {!today && <p className="text-sm mt-2">Elige la fecha de inicio y tus días de riego para ver aquí qué toca cada día.</p>}
    {streak > 0 && (
      <div data-testid="water-today-streak" className="mt-3 flex items-center gap-2 rounded-[10px] bg-[#8CD05A]/20 p-2">
        <motion.img src="/img/streak-monkey.webp" alt="El mono celebra tu racha" className="w-11 h-11 object-contain" animate={{ y: [0, -6, 0], rotate: [0, -8, 0] }}
          transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 0.8 }} style={{ transformOrigin: "50% 90%" }} />
        <p className="text-sm leading-tight"><b>Racha de {streak} {streak === 1 ? "riego" : "riegos"}</b><br /><span className="text-xs">seguidos sin olvidar ninguno</span></p>
      </div>
    )}
    <button onClick={() => { onClose(); scrollToId("riego"); }} data-testid="water-today-goto"
      className="mt-4 w-full h-10 rounded-full bg-[#0E2A1C] text-[#F6EEDC] text-sm font-bold hover:bg-[#1B3A8C] transition-colors">Ver cuadrante de riego</button>
  </motion.div>
);

export const JWaterToday = () => {
  const [cfg] = useGrow();
  const today = todayIrrigation(cfg);
  const [open, setOpen] = useState(false);
  const streak = useStreak(cfg);
  const imgs = [...new Set((today?.water ? today.doses : []).map((d) => WATERER[d.id]?.[0]).filter(Boolean))].slice(0, 3);
  return (
    <div className="absolute z-30 inset-x-0 top-[116px] md:top-[78px] pointer-events-none">
     <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col items-start gap-2">
     <div className="relative inline-block pointer-events-auto z-10">
      <AnimatePresence>{open && <Panel today={today} streak={streak} onClose={() => setOpen(false)} />}</AnimatePresence>
      <motion.button onClick={() => setOpen((o) => !o)} data-testid="water-today-button" data-state={!today ? "none" : today.water ? "water" : "rest"}
        aria-label={!today ? "Planifica tu riego" : today.water ? "Hoy toca regar" : "Hoy descansas"}
        initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.6, type: "spring", stiffness: 300, damping: 18 }}
        whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.95 }}
        className="relative flex items-center gap-1 h-12 md:h-16 rounded-full bg-[#0E2A1C]/80 backdrop-blur-md border border-[#8FB0FF]/60 p-0.5 sm:pl-1.5 sm:pr-3 shadow-[0_12px_30px_-10px_rgba(0,0,0,0.7)]">
        {imgs.length ? (
          <span className="flex -space-x-4">
            {imgs.map((src, i) => <motion.img key={src} {...pour(i)} src={src} alt="" className={`w-11 h-11 md:w-12 md:h-12 object-contain ${i ? "hidden sm:block" : ""}`} style={{ transformOrigin: "50% 90%" }} />)}
          </span>
        ) : (
          <span className="w-11 h-11 grid place-items-center rounded-full bg-[#1B3A8C]">{today ? <MoonStar className="w-5 h-5 text-[#F2B441]" /> : <Droplets className="w-5 h-5 text-[#8FB0FF]" />}</span>
        )}
        <span className="text-left leading-tight hidden sm:block">
          <span className="block font-mono-d text-[9px] tracking-[0.16em] uppercase text-[#8FB0FF]">Hoy</span>
          <span className="block text-xs font-bold text-[#F6EEDC]">{!today ? "Planifica" : today.water ? "Toca regar" : "Descanso"}</span>
        </span>
        {today?.water && <span className="absolute -top-1 -right-1 w-5 h-5 grid place-items-center rounded-full bg-[#5B7FE0] border-2 border-[#0E2A1C]"><Droplets className="w-3 h-3 text-white" /></span>}
        {streak > 0 && <span data-testid="streak-badge-mobile" className="sm:hidden absolute -bottom-1 -right-1 h-5 px-1 inline-flex items-center gap-0.5 rounded-full bg-[#8CD05A] text-[#0E2A1C] text-[10px] font-black border-2 border-[#0E2A1C]"><Flame className="w-2.5 h-2.5" />{streak}</span>}
      </motion.button>
     </div>
     <motion.button onClick={() => scrollToId("riego")} data-testid="irrigation-shortcut-button" aria-label="Abrir cuadrante de riegos"
       initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.8, duration: 0.5 }} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
       className="pointer-events-auto inline-flex items-center gap-2 h-10 md:h-11 rounded-full bg-[#0E2A1C]/80 backdrop-blur-md border border-[#F2B441]/60 p-0.5 sm:pl-1 sm:pr-3.5 shadow-[0_12px_30px_-10px_rgba(0,0,0,0.7)]">
       <span className="w-9 h-9 md:w-10 md:h-10 grid place-items-center rounded-full bg-[#F2B441] text-[#0E2A1C]"><CalendarDays className="w-4 h-4 md:w-5 md:h-5" /></span>
       <span className="hidden sm:block text-left leading-tight">
         <span className="block font-mono-d text-[9px] tracking-[0.16em] uppercase text-[#F2B441]">Cuadrante</span>
         <span className="block text-xs font-bold text-[#F6EEDC]">Riegos</span>
       </span>
     </motion.button>
     <JStreak cfg={cfg} />
     </div>
    </div>
  );
};
